/**
 * The lines of a bar: which staff each is on, which line of that staff it is, and where an
 * edit that works on one of them reaches.
 *
 * A bar holds its music as a flat list of voices, and every editing operation that adds or
 * takes one away needs the same three answers out of that list -- which of them belong to
 * the staff in hand, which one the caret is standing in, and which bars the edit covers.
 * They are here rather than in any one of those operations because `voices.ts` and
 * `ossia.ts` both work on the same list and must agree about it exactly: an ossia sits in
 * `measure.voices` beside the voices played from the staff it stands over, so a query that
 * counted it as one of them would spend one of the staff's four on music nobody plays.
 *
 * Nothing here changes a score. See `voices.ts` and `ossia.ts` for what does.
 */

import { rest } from "../document/create";
import { plain } from "../document/duration";
import type { EventId, MeasureId } from "../document/ids";
import { linesOn, standingIn } from "../document/standing";
import type { Standing } from "../document/standing";
import type { Command } from "../commands/types";
import type { Direction, MusicEvent, Part, PartMeasure, Score } from "../document/types";
import type { Caret } from "./entry-place";
import { silence } from "./entry-voice";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

export { standingIn } from "../document/standing";
export type { Standing } from "../document/standing";

/**
 * How many voices one staff may hold.
 *
 * Four is the engraving convention rather than a limit of the model: stems up, stems down,
 * and two more for the rare staff that needs them. Past that a staff is unreadable and the
 * music wants another one.
 */
export const MAX_VOICES = 4;

/** What an edit to a bar's lines comes to: one command, and where the caret then stands. */
export interface VoiceChange {
  readonly command: Command;
  readonly caret: Caret;
}

/**
 * Indices into `measure.voices` of the voices played from `staff`, in the order kept.
 *
 * An ossia stands on the staff as well, and is not one of them: it is an alternative to
 * what is played there rather than another line played with it, so counting it would spend
 * one of the four voices a staff may be written in. See `editor/ossia.ts`.
 */
export function voicesOn(measure: PartMeasure, staff: number): readonly number[] {
  return linesOn(measure, staff, false);
}

/** Indices into `measure.voices` of the ossias offered above `staff`, in the order kept. */
export function ossiasOn(measure: PartMeasure, staff: number): readonly number[] {
  return linesOn(measure, staff, true);
}

/**
 * The same voice of the same staff, one bar over.
 *
 * Bars hold different numbers of voices, so an index is not portable between them. What is
 * portable is the staff and which voice of that staff it is, and a bar that does not go
 * that deep lands the caret on its last voice rather than nowhere at all. A staff missing
 * from the other bar -- which a valid score does not have -- falls back to its first voice,
 * because a caret pointing at nothing is worse than one pointing low. A caret standing in
 * an ossia crosses into the next bar's ossia, and into what the staff plays where the next
 * bar offers none, which is most of them.
 */
export function sameVoice(from: PartMeasure, into: PartMeasure, voiceIndex: number): number {
  const last = Math.max(into.voices.length - 1, 0);
  const standing = standingIn(from, voiceIndex);
  if (standing === null) return Math.min(voiceIndex, last);
  const offered = standing.ossia ? ossiasOn(into, standing.staff) : [];
  const there = offered.length > 0 ? offered : voicesOn(into, standing.staff);
  if (there.length === 0) return Math.min(voiceIndex, last);
  return there[Math.min(standing.ordinal, there.length - 1)] ?? 0;
}

/**
 * The bars an edit reaches: the ones the selection touches in this part, else the caret's.
 *
 * Selecting a passage and asking for a voice gives the voice to the whole passage, which is
 * what makes a divided phrase typeable without asking again at every bar line.
 */
export function barsFor(score: Score, caret: Caret, selection: Selection): readonly MeasureId[] {
  const touched = new Set<MeasureId>();
  for (const at of selectedLocations(score, selection)) {
    if (at.partId === caret.partId) touched.add(at.measureId);
  }
  if (touched.size === 0) return [caret.measureId];
  return score.measures.map((global) => global.id).filter((id) => touched.has(id));
}

/** A bar of silence, as plain rests, or the bar's own rest when it cannot be written so. */
export function silentEvents(capacity: number): readonly MusicEvent[] {
  return silence(capacity) ?? [rest(plain("whole"), true)];
}

/** The part and bar a caret stands in, with which line of which staff that is. */
export interface Where extends Standing {
  readonly part: Part;
  readonly measure: PartMeasure;
}

/** Where a caret stands, or nothing when it is not standing in a line of a bar. */
export function whereIs(score: Score, caret: Caret): Where | null {
  const part = score.parts.find((candidate) => candidate.id === caret.partId);
  const measure = part?.measures[caret.measureId];
  const standing = measure === undefined ? null : standingIn(measure, caret.voiceIndex);
  if (part === undefined || measure === undefined || standing === null) return null;
  return { part, measure, ...standing };
}

/** Several commands as one undo step, or the single command when there is only one. */
export function asOne(commands: readonly Command[], label: string): Command | null {
  const only = commands[0];
  if (only === undefined) return null;
  return commands.length === 1 ? only : { kind: "batch", label, commands };
}

/** The directions of a bar that hang on none of `gone`. */
export function kept(
  directions: readonly Direction[],
  gone: ReadonlySet<EventId>,
): readonly Direction[] {
  return directions.filter((direction) => !gone.has(direction.anchor));
}
